import { AddContactRequest } from '~api/address_book';

import { Fragment, PropsWithChildren } from 'react';
import { Controller } from 'react-hook-form';

import { Add16, Bin16 } from '@ingrid/iconography/react';
import { ButtonLink, Card, FormItem, IconButton, ListLayout, TextInput } from '@ingrid/luterkort-react';

import { CountrySelect } from '~src/components/CountrySelect/CountrySelect';
import { FormLeaveBlocker } from '~src/components/FormLeaveBlocker/FormLeaveBlocker';
import { useTranslation } from '~src/i18n/use-translation';
import { removeByIndex } from '~src/utils/array';

import { useAddressBookFormContext } from '../hooks';
import { Validate } from './validate';

export type FormValues = Omit<AddContactRequest, 'site_id'> & { id?: string };

function FormRow({ children }: PropsWithChildren) {
  return <div style={{ display: 'flex', gap: '1.25rem' }}>{children}</div>;
}

export function AddressBookForm() {
  const { formState, register, control, watch, setValue, getFieldState } = useAddressBookFormContext();
  const { t } = useTranslation();

  const addressLines = watch('address.address_lines') ?? [];
  const disableAppendAddressLine = addressLines.length >= 4 || addressLines[addressLines.length - 1] === '';

  return (
    <Fragment>
      <Card.Wrapper>
        <Card.Header>{t('common.basicInformation')}</Card.Header>

        <Card.Content>
          <FormItem.Root>
            <FormItem.Label>{t('common.name')}</FormItem.Label>
            <FormItem.Control invalid={!!formState.errors.address?.name?.message}>
              <TextInput {...register('address.name', Validate().name)} aria-required="true" />
            </FormItem.Control>
            {formState.errors.address?.name?.message && (
              <FormItem.ErrorText>{formState.errors.address.name.message}</FormItem.ErrorText>
            )}
          </FormItem.Root>
        </Card.Content>
      </Card.Wrapper>

      <Card.Wrapper>
        <Card.Header>{t('common.address')}</Card.Header>

        <Card.Content>
          <Card.List>
            <ListLayout.Column style={{ display: 'flex', gap: '16px' }}>
              {addressLines.map((_, index) => {
                const addressLineFieldState = getFieldState(`address.address_lines.${index}`);

                return (
                  <FormItem.Root style={{ width: '100%' }} key={`field-arr-${index}`}>
                    <FormItem.Label>{t('common.addressLineIndex', { index: index + 1 })}</FormItem.Label>
                    <FormItem.Control invalid={addressLineFieldState.invalid}>
                      <ListLayout.Stack>
                        <ListLayout.Row style={{ gap: '16px' }}>
                          {/* Cannot use Validate() here, because it counts as a hook, which react doesn't like. */}
                          {/* Conditionally rendering hooks is not allowed by React, which this would result in. */}
                          <TextInput
                            data-invalid={addressLineFieldState.invalid}
                            {...register(`address.address_lines.${index}`, {
                              required:
                                index === 0 ? t('common.fieldRequired', { field: t('common.addressLine') }) : false,
                            })}
                          />

                          <IconButton
                            aria-label={t('common.removeItem', { item: 1 })}
                            onClick={() => setValue('address.address_lines', removeByIndex(addressLines, index))}
                            style={{ flexShrink: '0' }}
                            disabled={index === 0}
                            variant="secondary"
                          >
                            <Bin16 />
                          </IconButton>
                        </ListLayout.Row>
                      </ListLayout.Stack>
                    </FormItem.Control>

                    {addressLineFieldState.error?.message && (
                      <FormItem.ErrorText>{addressLineFieldState.error.message}</FormItem.ErrorText>
                    )}
                  </FormItem.Root>
                );
              })}

              <div>
                <ButtonLink
                  onClick={() => setValue('address.address_lines', [...addressLines, ''])}
                  disabled={disableAppendAddressLine}
                >
                  <Add16 />
                  {t('common.addAddressLine')}
                </ButtonLink>
              </div>
            </ListLayout.Column>

            <FormItem.Root>
              <FormItem.Label>{`${t('common.doorCode')} (${t('common.optional')})`}</FormItem.Label>
              <FormItem.Control invalid={!!formState.errors.address?.door_code?.message}>
                <TextInput {...register('address.door_code')} />
              </FormItem.Control>
              {formState.errors.address?.door_code?.message && (
                <FormItem.ErrorText>{formState.errors.address.door_code.message}</FormItem.ErrorText>
              )}
            </FormItem.Root>

            <FormRow>
              <FormItem.Root css={{ maxWidth: '120px', minWidth: '80px' }}>
                <FormItem.Label>{t('common.postalCode')}</FormItem.Label>
                <FormItem.Control invalid={!!formState.errors.address?.postal_code?.message}>
                  <TextInput {...register('address.postal_code', Validate().postal_code)} aria-required="true" />
                </FormItem.Control>
                {formState.errors.address?.postal_code?.message && (
                  <FormItem.ErrorText>{formState.errors.address.postal_code.message}</FormItem.ErrorText>
                )}
              </FormItem.Root>

              <FormItem.Root css={{ width: '100%' }}>
                <FormItem.Label>{t('common.city')}</FormItem.Label>
                <FormItem.Control invalid={!!formState.errors.address?.city?.message}>
                  <TextInput {...register('address.city', Validate().city)} aria-required="true" />
                </FormItem.Control>
                {formState.errors.address?.city?.message && (
                  <FormItem.ErrorText>{formState.errors.address.city.message}</FormItem.ErrorText>
                )}
              </FormItem.Root>
            </FormRow>

            <FormRow>
              <FormItem.Root css={{ width: '50%' }}>
                <FormItem.Label>{`${t('common.region')} (${t('common.optional')})`}</FormItem.Label>
                <FormItem.Control invalid={!!formState.errors.address?.region?.message}>
                  <TextInput {...register('address.region')} />
                </FormItem.Control>
                {formState.errors.address?.region?.message && (
                  <FormItem.ErrorText>{formState.errors.address.region.message}</FormItem.ErrorText>
                )}
              </FormItem.Root>

              <FormItem.Root css={{ width: '50%' }}>
                <FormItem.Label>{t('common.country')}</FormItem.Label>
                <Controller
                  control={control}
                  name="address.country"
                  render={({ field }) => (
                    <FormItem.Control>
                      <CountrySelect
                        {...field}
                        invalid={!!formState.errors.address?.country?.message}
                        onChange={value => {
                          field.onChange(value);
                          field.onBlur();
                        }}
                      />
                    </FormItem.Control>
                  )}
                  rules={Validate().country}
                />
                {formState.errors.address?.country?.message && (
                  <FormItem.ErrorText>{formState.errors.address.country.message}</FormItem.ErrorText>
                )}
              </FormItem.Root>
            </FormRow>
          </Card.List>
        </Card.Content>
      </Card.Wrapper>

      <Card.Wrapper>
        <Card.Header>{t('addressBook.details.contactInformation')}</Card.Header>

        <Card.Content>
          <Card.List>
            <FormItem.Root>
              <FormItem.Label>{`${t('common.phone')} (${t('common.optional')})`}</FormItem.Label>
              <FormItem.Control invalid={!!formState.errors.phone?.message}>
                <TextInput {...register('phone', Validate().phone)} />
              </FormItem.Control>
              {formState.errors.phone?.message && (
                <FormItem.ErrorText>{formState.errors.phone.message}</FormItem.ErrorText>
              )}
            </FormItem.Root>

            <FormItem.Root>
              <FormItem.Label>{`${t('common.email')} (${t('common.optional')})`}</FormItem.Label>
              <FormItem.Control invalid={!!formState.errors.email?.message}>
                <TextInput {...register('email', Validate().email)} />
              </FormItem.Control>
              {formState.errors.email?.message && (
                <FormItem.ErrorText>{formState.errors.email.message}</FormItem.ErrorText>
              )}
            </FormItem.Root>
          </Card.List>
        </Card.Content>
      </Card.Wrapper>

      <FormLeaveBlocker formState={formState} />
    </Fragment>
  );
}
